import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";

import "./styles/SignUp.css";

import signUpBackground from "../medias/png/background/signUp_background.png";
import signInImage from "../medias/png/background/signin_background.png";

const SignUp = () => {
  const { t } = useTranslation();

  const [showSignUp, setShowSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pseudo, setPseudo] = useState("");
  const [name, setName] = useState("");
  const [nationality, setNationality] = useState("FRANCE");
  const [knowledge, setKnowledge] = useState("AMATEUR");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [countries, setCountries] = useState([]);

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiRestCountriesURL = process.env.REACT_APP_REST_COUNTRIES;
  const { user, LogIn } = useAuth();

  useEffect(() => {
    if (user) {
      navigate("/profil");
    }
  }, [user, navigate]);

  const handleStartClick = () => {
    setShowSignUp(true);
  };

  const tooglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleEmailChange = (event) => setEmail(event.target.value);
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handlePseudoChange = (event) => setPseudo(event.target.value);
  const handleNameChange = (event) => setName(event.target.value);
  const handleNationalityChange = (event) => setNationality(event.target.value);
  const handleKnowledgeChange = (event) => setKnowledge(event.target.value);

  useEffect(() => {
    fetch(apiRestCountriesURL)
      .then((response) => response.json())
      .then((data) => {
        const countryOptions = data.map((country) => ({
          name: country.translations?.fra?.common || country.name?.common || country.cca2,
          code: country.cca2,
        }));
        setCountries(countryOptions);
      })
      .catch((error) => console.error("Erreur lors de la récupération des pays:", error));
  }, [apiRestCountriesURL]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const emailRegex = /^[\w-.]+@([\w-]+.)+[\w-]{2,4}$/;
    if (!emailRegex.test(email)) {
      alert(t("signup.alertInvalidEmail"));
      return;
    }

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(password)) {
      alert(t("signup.alertInvalidPassword"));
      return;
    }

    const userData = { email, password, pseudo, name, nationality, knowledge };

    try {
      const response = await fetch(`${apiUrl}/sign-up`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("signup.alertGenericError"));
      }

      const userId = data.userId;
      const accountType = data.accountType;
      LogIn(userId, accountType);

      navigate("/profil");
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      alert(error.message);
    }
  };

  const renderSignUpForm = () => (
    <div className="sign-up-form-container">
      <img
        src={signUpBackground}
        className="sign-up-background"
        alt={t("signup.altRunnerPose")}
        loading="lazy"
        width="327"
        height="375"
      />

      <form className="sign-up-form" onSubmit={handleSubmit}>
        <h1 className="sign-up-form-title">{t("signup.formTitle")}</h1>

        <div className="sign-up-form-row">
          <div className="sign-up-form-control">
            <label htmlFor="pseudo">{t("signup.labels.pseudo")}</label>
            <input id="pseudo" type="text" onChange={handlePseudoChange} required />
          </div>

          <div className="sign-up-form-control">
            <label htmlFor="name">{t("signup.labels.name")}</label>
            <input id="name" type="text" onChange={handleNameChange} required />
          </div>
        </div>

        <div className="sign-up-form-row">
          <div className="sign-up-form-control">
            <label htmlFor="email">{t("signup.labels.email")}</label>
            <input id="email" type="email" value={email} onChange={handleEmailChange} required />
          </div>

          <div className="sign-up-form-control">
            <label htmlFor="nationality">{t("signup.labels.nationality")}</label>
            <select id="nationality" onChange={handleNationalityChange} value={nationality}>
              <option value="france">{t("signup.nationalityPlaceholder")}</option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="sign-up-form-row">
          <div className="sign-up-form-control">
            <label htmlFor="knowledge">{t("signup.labels.knowledge")}</label>
            <select id="knowledge" onChange={handleKnowledgeChange} value={knowledge}>
              <option value="amateur">{t("signup.knowledge.amateur")}</option>
              <option value="pro">{t("signup.knowledge.pro")}</option>
              <option value="expert">{t("signup.knowledge.expert")}</option>
            </select>
          </div>

          <div className="sign-up-form-control">
            <label htmlFor="password">{t("signup.labels.password")}</label>
            <input
              id="password"
              type={passwordVisible ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              required
            />
            <img
              src={`${process.env.PUBLIC_URL}/icons/eye.png`}
              alt={t("signup.altEyeIcon")}
              className="sign-up-form-visibility-icon"
              width="20"
              height="20"
              onClick={tooglePasswordVisibility}
            />
          </div>
        </div>

        <div className="sign-up-form-row">
          <label className="sign-up-form-label-condition">
            <input type="checkbox" required />
            {t("signup.acceptPrefix")} <strong>{t("signup.acceptStrong")}</strong>
          </label>
        </div>

        <div className="sign-up-form-row button-row">
          <button type="submit">{t("signup.submit")}</button>
        </div>
      </form>
    </div>
  );

  return (
    <main className="sign-up">
      {!showSignUp ? (
        <div className="sign-up-header">
          <img
            src={signInImage}
            alt={t("signup.altCyclistDownhill")}
            loading="lazy"
            width="2000"
            height="1333"
          />
          <h1 className="sign-up-title">{t("signup.headerTitle")}</h1>

          <div className="sign-up-content">
            <p>{t("signup.headerDescription")}</p>
            <button onClick={handleStartClick}>{t("signup.cta")}</button>

            <p className="sign-up-login-link">
              {t("signup.alreadyAccount")} <a href="/login">{t("signup.loginLink")}</a>
            </p>
          </div>
        </div>
      ) : (
        renderSignUpForm()
      )}
    </main>
  );
};

export default SignUp;
